import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { icon } from '@Root/lib/lit-3form-block/icons';
import { imagebossUrl } from '@Helpers/imageboss';
import {
  DEFAULT_LEVEL_INDEX,
  DEFAULT_SELECTION,
  ETCH_TARGETS,
  OPACITY_LEVELS,
  PRINT_MODES,
  configuredFilename,
} from '@Helpers/pattern-recolor';
import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics';
import { createToolAnalytics } from '../../../../helpers/tool-analytics';
import { styles } from './css';

import '../../../form-controls/hue-field-picker';
import '@Root/lib/lit-3form-block/components/cta-button';
import '@Root/lib/lit-3form-block/components/cta-text';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';
import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
import { a11yStyles } from '../../../form-controls/a11y-styles';

// The design's "Pattern Material" toggle is two groups; anything that is not
// Varia is a glass.
const MATERIAL_GROUPS = [
  { key: 'varia', label: 'Varia', matches: slug => slug === 'varia' },
  { key: 'glass', label: 'Glass', matches: slug => slug !== 'varia' },
];

const PRINT_METHOD_LABELS = {
  digital_print: 'Digital Print',
  etched_glass: 'Etched Glass',
};

// Query string contract. Scale and inverse are decided; the rest follow the
// same shape so a configured page is shareable. `background` is only read,
// for links made when the background could carry the color.
const QUERY = {
  scale: 'scale',
  inverse: 'inverse',
  print: 'print',
  pattern: 'pattern',
  background: 'background',
  opacity: 'opacity',
};

// A picker drag syncs on every move, and Safari throws once a page calls
// replaceState 100 times in a few seconds, so the URL waits for a pause.
const QUERY_SYNC_DELAY_MS = 300;

const HERO_IMAGE_HEIGHT = 1000;
// The hero zooms to 2x and a CSS mask rasterizes from the file it was handed,
// so the zoomed mask needs a source at twice the hero height.
const HERO_ZOOM_IMAGE_HEIGHT = HERO_IMAGE_HEIGHT * 2;
const THUMB_IMAGE_WIDTH = 100;
// Long edge of the image the configured download is rendered from. Served
// by ImageBoss rather than S3 because the canvas needs CORS headers, and
// capped because some library panels are over 200 megapixels.
const EXPORT_IMAGE_HEIGHT = 4000;

export class GraphicPatternDesignerBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      _material: { type: String },
      _printMethod: { type: String },
      _variantKey: { type: String },
      _color: { type: String },
      _level: { type: Number },
      _downloading: { type: Boolean },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      a11yStyles,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content') || '{}');
    this.toolAnalytics = createToolAnalytics('Graphic Pattern Designer');
    this._initialized = false;
    this._material = 'varia';
    this._printMethod = 'digital_print';
    this._variantKey = '';
    // Graphic patterns are specified as one color on the pattern (Tolman
    // Bryant, digital printing); an inverse variant is how the background
    // takes it. The color outlives a switch to etched glass.
    this._color = DEFAULT_SELECTION.foreground;
    this._level = DEFAULT_LEVEL_INDEX;
    this._downloading = false;
  }

  // Inside Walter the element is upgraded before Lit commits the bound
  // content attribute, so derived state has to wait for the data to land.
  willUpdate(changedProperties) {
    if (
      !this._initialized &&
      changedProperties.has('content') &&
      this.variants.length > 0
    ) {
      this._initialized = true;
      this.initializeFromContent();
    }
  }

  initializeFromContent() {
    const defaults = this.designer.defaults || {};
    this._material = this.materialGroups[0]?.key || 'varia';
    this._printMethod = this.availablePrintMethods[0] || 'digital_print';
    this._variantKey = this.designer.default_variant_key || '';
    this._color = defaults.foreground || DEFAULT_SELECTION.foreground;
    this.applyQueryParams();
    this.toolAnalytics.observe(this);
    this.updateComplete.then(() => this.emitSelection());
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  get designer() {
    return this.content?.graphic_pattern_designer || {};
  }

  get variants() {
    return this.designer.variants || [];
  }

  get printMethods() {
    return this.designer.print_methods || [];
  }

  get selectedVariant() {
    return (
      this.variants.find(variant => variant.key === this._variantKey) ||
      this.variants[0] ||
      null
    );
  }

  get materialGroups() {
    const slugs = this.printMethods.flatMap(method =>
      method.materials.map(material => material.slug)
    );
    return MATERIAL_GROUPS.filter(group => slugs.some(group.matches));
  }

  // Digital print is offered on every material; etching only on glass.
  get availablePrintMethods() {
    return this.printMethods.map(method => method.key);
  }

  get hasEtchedGlass() {
    return this.availablePrintMethods.includes('etched_glass');
  }

  get isEtched() {
    return this._printMethod === 'etched_glass';
  }

  get scales() {
    return [...new Set(this.variants.map(variant => variant.scale))];
  }

  get hasInverse() {
    return this.variants.some(variant => variant.is_inverse);
  }

  get selectedIsInverse() {
    return !!this.selectedVariant?.is_inverse;
  }

  variantFor(scale, inverse) {
    return this.variants.find(
      variant => variant.scale === scale && !!variant.is_inverse === inverse
    );
  }

  get selection() {
    return this.selectionFor(this.sourceImage);
  }

  selectionFor(image) {
    const alpha = OPACITY_LEVELS[this._level].alpha;
    return {
      mode: this.isEtched
        ? PRINT_MODES.ETCHED_GLASS
        : PRINT_MODES.DIGITAL_PRINT,
      foreground: this._color,
      foregroundAlpha: alpha,
      background: null,
      backgroundAlpha: alpha,
      // Inverse variants are separate assets, so the mask is not flipped
      // for them. It is flipped for knock-out images, whose alpha covers
      // the ground rather than the ink.
      inverse: !!image?.alpha_is_ground,
      etchTarget: ETCH_TARGETS.PATTERN,
    };
  }

  get sourceImage() {
    const variant = this.selectedVariant;
    return variant?.full_panel || variant?.swatch || null;
  }

  // The variant behind the swatch slide: the selected one, once its library
  // swatch exists. The legacy crop is the panel's stand-in, not a swatch.
  get swatchVariant() {
    const variant = this.selectedVariant;
    return variant?.swatch && !variant.swatch.legacy ? variant : null;
  }

  get patternName() {
    return this.designer.pattern?.name || this.content.headerStr || '';
  }

  // What reports call this configuration. Colors and opacity are left out on
  // purpose: they are session detail, not a segment.
  get analyticsLabel() {
    return this.analyticsLabelFor(this.selectedVariant);
  }

  analyticsLabelFor(variant) {
    const parts = [this.patternName, variant?.name];
    if (this.isEtched) {
      parts.push('Etched Glass', 'Etch Pattern');
    } else {
      parts.push(
        'Digital Print',
        this._material === 'varia' ? 'Varia' : 'Glass'
      );
    }
    return parts.filter(Boolean).join(' ');
  }

  // Everything a rep needs to reproduce the design, for the quote request
  // and the visualization lead. Colors belong here, not in analytics.
  get leadDetails() {
    const level = index => OPACITY_LEVELS[index].level;
    const selection = this.selection;
    return {
      pattern: this.patternName,
      variant: this.selectedVariant?.name || '',
      printMethod: PRINT_METHOD_LABELS[this._printMethod] || this._printMethod,
      material: this._material === 'varia' ? 'Varia' : 'Glass',
      etchTarget: this.isEtched ? ETCH_TARGETS.PATTERN : null,
      patternColor: this.isEtched ? null : selection.foreground || 'clear',
      patternOpacity: this.isEtched ? null : level(this._level),
      backgroundColor: this.isEtched ? null : selection.background || 'clear',
      backgroundOpacity: this.isEtched ? null : level(this._level),
    };
  }

  // An image through ImageBoss, no more than `limit` tall and never larger
  // than the original.
  imageUrlAt(image, limit) {
    if (!image) return null;
    return imagebossUrl(image.src, {
      height: image.height ? Math.min(image.height, limit) : limit,
    });
  }

  // Source for the canvas export.
  get exportUrl() {
    return this.imageUrlAt(this.sourceImage, EXPORT_IMAGE_HEIGHT);
  }

  // Source for the zoomed hero mask.
  get zoomUrl() {
    return this.imageUrlAt(this.sourceImage, HERO_ZOOM_IMAGE_HEIGHT);
  }

  // Every URL and size a carousel slide needs to paint one image as a mask.
  imageSources(image) {
    return {
      imageUrl: imagebossUrl(image.src, { height: HERO_IMAGE_HEIGHT }),
      thumbUrl: imagebossUrl(image.src, { width: THUMB_IMAGE_WIDTH }),
      zoomUrl: this.imageUrlAt(image, HERO_ZOOM_IMAGE_HEIGHT),
      fullResolutionUrl: image.full_resolution_src,
      exportUrl: this.imageUrlAt(image, EXPORT_IMAGE_HEIGHT),
      width: image.width,
      height: image.height,
    };
  }

  // ---------------------------------------------------------------------
  // URL state
  // ---------------------------------------------------------------------

  applyQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const hex = value =>
      typeof value === 'string' && /^#?[0-9a-f]{6}$/i.test(value)
        ? '#' + value.replace('#', '').toLowerCase()
        : undefined;

    const scale = (params.get(QUERY.scale) || '').toUpperCase();
    const inverse = ['1', 'true'].includes(params.get(QUERY.inverse) || '');
    const variant =
      this.variantFor(scale, inverse) ||
      (scale && this.variantFor(scale, false));
    if (variant) this._variantKey = variant.key;

    const print = params.get(QUERY.print);
    if (
      print === 'etched' &&
      this.printMethods.some(m => m.key === 'etched_glass')
    ) {
      this._material = 'glass';
      this._printMethod = 'etched_glass';
    }

    const color =
      hex(params.get(QUERY.pattern)) || hex(params.get(QUERY.background));
    if (color) this._color = color;

    const levelIndex = OPACITY_LEVELS.findIndex(
      level => level.level === (params.get(QUERY.opacity) || '').toUpperCase()
    );
    if (levelIndex >= 0) this._level = levelIndex;
  }

  syncQueryParams() {
    clearTimeout(this._querySyncTimeout);
    this._querySyncTimeout = setTimeout(
      () => this.writeQueryParams(),
      QUERY_SYNC_DELAY_MS
    );
  }

  writeQueryParams() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const variant = this.selectedVariant;

    if (variant) {
      params.set(QUERY.scale, variant.scale.toLowerCase());
      if (variant.is_inverse) params.set(QUERY.inverse, '1');
      else params.delete(QUERY.inverse);
    }

    if (this.isEtched) {
      params.set(QUERY.print, 'etched');
      params.delete(QUERY.pattern);
      params.delete(QUERY.background);
      params.delete(QUERY.opacity);
    } else {
      params.delete(QUERY.print);
      params.delete(QUERY.background);
      params.set(QUERY.pattern, this._color.replace('#', ''));
      params.set(QUERY.opacity, OPACITY_LEVELS[this._level].level);
    }

    window.history.replaceState(window.history.state, '', url);
  }

  // ---------------------------------------------------------------------
  // Selection changes
  // ---------------------------------------------------------------------

  emitSelection() {
    const variant = this.selectedVariant;
    const image = this.sourceImage;
    if (!variant || !image) return;

    const detail = {
      selection: this.selection,
      ...this.imageSources(image),
      variant,
      patternName: this.patternName,
      variantName: variant.name,
      printMethod: this._printMethod,
      material: this._material,
      filename: configuredFilename(
        this.patternName,
        variant.name,
        this.selection
      ),
      analyticsLabel: this.analyticsLabel,
      lead: this.leadDetails,
      swatch: this.swatchDetail,
    };

    this.dispatchEvent(
      new CustomEvent('graphicPatternDesignerUpdated', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  // The swatch slide, shaped like the panel detail so the carousel paints
  // and downloads both the same way. Null until the library swatch exists.
  get swatchDetail() {
    const variant = this.swatchVariant;
    if (!variant) return null;

    const image = variant.swatch;
    return {
      selection: this.selectionFor(image),
      ...this.imageSources(image),
      variant,
      patternName: this.patternName,
      variantName: variant.name,
      filename: configuredFilename(
        this.patternName,
        `${variant.name} swatch`,
        this.selectionFor(image)
      ),
      analyticsLabel: this.analyticsLabelFor(variant),
    };
  }

  changed(label) {
    this.syncQueryParams();
    this.emitSelection();
    if (label) this.sendSelectionAnalyticsEvent(label);
  }

  // Etching is a glass process: Varia is always digital print and offers
  // no design options.
  selectMaterial(key) {
    if (key === this._material) return;
    this._material = key;
    if (key === 'varia' && this.isEtched) this._printMethod = 'digital_print';
    this.changed(`Material-${key}`);
  }

  get showPrintMethods() {
    return this._material === 'glass' && this.printMethods.length > 1;
  }

  selectPrintMethod(key) {
    if (key === this._printMethod) return;
    if (key === 'etched_glass' && this._material === 'varia') return;
    this._printMethod = key;
    this.changed(`Print-${key}`);
  }

  selectScale(scale) {
    const variant =
      this.variantFor(scale, this.selectedIsInverse) ||
      this.variantFor(scale, false);
    if (!variant || variant.key === this._variantKey) return;
    this._variantKey = variant.key;
    this.changed(`Scale-${variant.key}`);
  }

  selectInverse(inverse) {
    const variant = this.variantFor(this.selectedVariant?.scale, inverse);
    if (!variant || variant.key === this._variantKey) return;
    this._variantKey = variant.key;
    this.changed(`Inverse-${inverse}`);
  }

  setColor(value, commit = false) {
    this._color = value;
    this.syncQueryParams();
    this.emitSelection();
    if (commit) this.sendSelectionAnalyticsEvent('Color');
  }

  selectLevel(index) {
    this._level = index;
    this.changed(`Opacity-${OPACITY_LEVELS[index].level}`);
  }

  onPickerInput(event) {
    this.setColor(event.target.value);
  }

  onHexChange(event) {
    const value = event.target.value.trim().replace('#', '');
    if (/^[0-9a-f]{6}$/i.test(value)) {
      this.setColor('#' + value.toLowerCase(), true);
    } else {
      // Put the current color back rather than leaving junk in the field.
      event.target.value = this._color.replace('#', '');
    }
  }

  onPickerChange(event) {
    this.setColor(event.target.value, true);
  }

  downloadConfiguredImage(event) {
    event.preventDefault();
    if (this._downloading) return;
    this._downloading = true;
    this.dispatchEvent(
      new CustomEvent('downloadConfiguredImageFromBarrel', {
        bubbles: true,
        composed: true,
        detail: {
          done: () => {
            this._downloading = false;
          },
        },
      })
    );
  }

  // The button carries no URL so it cannot navigate on its own; Walter
  // stores the design for the quote form, records the NAV event and then
  // leaves the page.
  requestAQuote() {
    // Walter puts the page URL in the quote, so a pending sync lands first.
    clearTimeout(this._querySyncTimeout);
    this.writeQueryParams();
    this.sendCtaAnalyticsEvent('Request a Quote');
    this.dispatchEvent(
      new CustomEvent('requestMoreInfoFromBarrel', {
        bubbles: true,
        composed: true,
        detail: {
          cta: 'Request a Quote',
          url: '/request-quote',
          analyticsLabel: this.analyticsLabel,
          lead: this.leadDetails,
          image: this.sourceImage,
          imageUrl: imagebossUrl(this.sourceImage?.src, {
            height: HERO_IMAGE_HEIGHT,
          }),
          selection: this.selection,
        },
      })
    );
  }

  // The button opens the rep modal itself; this only records it.
  contactARep() {
    this.sendCtaAnalyticsEvent('Contact a Rep');
    fireAdobeAnalyticsEvent({
      type: 'click',
      detail: { eVar8: 'INP|Graphic Pattern Designer|Contact a Rep' },
    });
  }

  sendSelectionAnalyticsEvent(label) {
    this.toolAnalytics.select(label);
  }

  // CTAs and downloads carry the configuration they were clicked with.
  sendCtaAnalyticsEvent(cta) {
    this.toolAnalytics.action(cta, this.analyticsLabel);
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  get title() {
    return (this.content.title || 'Design Your Own').toUpperCase();
  }

  render() {
    return html`
      <div class="barrel-wrapper print-none">
        <div class="barrel-header">
          <span class="barrel-heavy-text type-barrel-title">
            <span>${this.title}</span>
          </span>
        </div>
        <div class="sections">
          ${this.materialSectionHTML}
          ${this.showPrintMethods ? this.printMethodSectionHTML : nothing}
          ${this.scaleSectionHTML}
          ${this.isEtched ? nothing : this.colorSectionHTML}
        </div>
        ${this.isEtched
          ? nothing
          : html`
              <p class="rep-note">
                Want to customize further options? Get in touch with a rep.
              </p>
            `}
        <div class="cta-row">
          <cta-button
            cta-data=${JSON.stringify({
              text: 'Request a Quote',
              standardWidth: true,
            })}
            cta-type="primary"
            @click="${this.requestAQuote}"
          ></cta-button>
          <cta-button
            cta-data=${JSON.stringify({
              text: 'Contact a Rep',
              url: '#contact-rep',
              standardWidth: true,
            })}
            cta-type="secondary"
            @click="${this.contactARep}"
          ></cta-button>
        </div>
        ${this.isEtched ? this.downloadsHTML : nothing}
      </div>
    `;
  }

  segmentHTML({ label, selected, onSelect }) {
    return html`
      <button
        type="button"
        class=${classMap({ segment: true, 'is-selected': selected })}
        aria-pressed=${selected ? 'true' : 'false'}
        @click="${onSelect}"
      >
        ${label}
      </button>
    `;
  }

  get materialSectionHTML() {
    const groups = this.materialGroups;
    if (groups.length === 0) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Material</p>
        <div class="segments" role="group" aria-label="Material">
          ${groups.map(group =>
            this.segmentHTML({
              label: group.label,
              selected: this._material === group.key,
              onSelect: () => this.selectMaterial(group.key),
            })
          )}
        </div>
      </div>
    `;
  }

  get printMethodSectionHTML() {
    const methods = this.printMethods.map(method => method.key);
    if (methods.length === 0) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Design Options</p>
        <div class="segments" role="group" aria-label="Design Options">
          ${methods.map(key =>
            this.segmentHTML({
              label: PRINT_METHOD_LABELS[key] || key,
              selected: this._printMethod === key,
              onSelect: () => this.selectPrintMethod(key),
            })
          )}
        </div>
      </div>
    `;
  }

  // A single-scale pattern has nothing to choose, so the scale control and
  // its label stay out; the section still carries Standard/Inverse.
  get scaleSectionHTML() {
    const scales = this.scales;
    const hasScales = scales.length > 1;
    if (!hasScales && !this.hasInverse) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Pattern Options</p>
        ${hasScales
          ? html`
              <div class="range-group">
                <div class="range-labels" aria-hidden="true">
                  <span>Small</span>
                  <span>Large</span>
                </div>
                <div class="segments" role="group" aria-label="Pattern Scale">
                  ${scales.map(scale =>
                    this.segmentHTML({
                      label: scale,
                      selected: this.selectedVariant?.scale === scale,
                      onSelect: () => this.selectScale(scale),
                    })
                  )}
                </div>
              </div>
            `
          : nothing}
        ${this.hasInverse
          ? html`
              <div class="segments" role="group" aria-label="Pattern Style">
                ${this.segmentHTML({
                  label: 'Standard',
                  selected: !this.selectedIsInverse,
                  onSelect: () => this.selectInverse(false),
                })}
                ${this.segmentHTML({
                  label: 'Inverse',
                  selected: this.selectedIsInverse,
                  onSelect: () => this.selectInverse(true),
                })}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  get colorSectionHTML() {
    return html`
      <div class="section">
        <p class="section-label">Color</p>
        <div class="picker">
          <hue-field-picker
            label="Color"
            .value=${this._color}
            @picker-input="${this.onPickerInput}"
            @picker-change="${this.onPickerChange}"
          ></hue-field-picker>
          <div class="hex-fields">
            <span class="hex-fields__format" aria-hidden="true">Hex</span>
            <label class="hex-fields__value">
              <span class="visually-hidden">Hex color</span>
              <span class="hex-fields__hash" aria-hidden="true">#</span>
              <input
                type="text"
                inputmode="text"
                maxlength="6"
                spellcheck="false"
                .value=${this._color.replace('#', '')}
                @change="${this.onHexChange}"
              />
            </label>
          </div>
          <div class="opacity-levels">
            <p class="section-label">Opacity Level</p>
            <div class="range-labels" aria-hidden="true">
              <span>Less Opaque</span>
              <span>More Opaque</span>
            </div>
            <div
              class="segments is-full"
              role="group"
              aria-label="Opacity Level"
            >
              ${OPACITY_LEVELS.map((level, index) =>
                this.segmentHTML({
                  label: level.level,
                  selected: this._level === index,
                  onSelect: () => this.selectLevel(index),
                })
              )}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Etched glass has no picker to fill the column, so the configured image
  // download moves up here next to any CMS downloads. Walter does the
  // export, the same as the carousel's own button.
  get downloadsHTML() {
    const downloads = this.content.downloads || [];
    return html`
      <div class="download-links">
        <a
          href="#"
          class="download-link"
          aria-busy=${this._downloading ? 'true' : 'false'}
          @click="${this.downloadConfiguredImage}"
        >
          <span class="download-link__icon">${icon('download')}</span>
          <span class="download-link__text">
            ${this._downloading
              ? 'Preparing Image…'
              : 'Download Configured Image'}
          </span>
        </a>
        ${downloads.map(
          download => html`
            <cta-text
              cta-data=${JSON.stringify(download)}
              hide-right-arrow-svg="true"
              target-blank="true"
              left-download-svg="true"
            ></cta-text>
          `
        )}
      </div>
    `;
  }
}

customElements.define(
  'graphic-pattern-designer-barrel',
  GraphicPatternDesignerBarrel
);
